/**
 * FormTemplateContext.js
 *
 * Provides patient-first-name template resolution to form renderers. Adult Autism
 * informant forms store {{patientFirstName}} tokens in their definitions; this
 * context supplies a bound resolveDisplayText() function so GenericFormPage can
 * inject the real name without knowing about informant storage paths.
 *
 * Patient forms do not use this context — GenericFormPage falls back to rendering
 * text unchanged when no provider is present.
 */
import React, { createContext, useContext, useMemo } from 'react';
import { createTemplateTextResolver } from '../utils/resolveFormTemplates';

export const FormTemplateContext = createContext(null);

/**
 * @param {object} props
 * @param {string} props.patientFirstName - Live patient first name for injection
 * @param {React.ReactNode} props.children
 */
export function FormTemplateProvider({ patientFirstName, children }) {
  const value = useMemo(() => ({
    patientFirstName: patientFirstName || '',
    resolveDisplayText: createTemplateTextResolver(patientFirstName),
  }), [patientFirstName]);

  return (
    <FormTemplateContext.Provider value={value}>
      {children}
    </FormTemplateContext.Provider>
  );
}

/**
 * Returns resolveDisplayText from context, or an identity function when absent.
 */
export function useFormTemplateResolver() {
  const ctx = useContext(FormTemplateContext);
  return ctx?.resolveDisplayText || ((text) => text);
}

export function usePatientFirstNameForTemplates() {
  const ctx = useContext(FormTemplateContext);
  return ctx?.patientFirstName || '';
}
